import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DurationUnitType } from 'dayjs/plugin/duration.js';

// UIKit Components
import { Box, FlexBox, IconButton, IconButtonProps, Typography } from '@spazious/react-components';
import { IconChevronDown, IconChevronUp } from '@spazious/react-icons';
import { styled } from '@spazious/react-styles';

import DurationSelectorCustomInput, {
  DurationSelectorCustomInputProps,
} from './DurationSelectorCustomInput.js';

export class DurationChangeEvent<TOriginalEvent> extends Event {
  constructor(
    public readonly value: number,
    public readonly unit: DurationUnitType,
    public readonly label: string,
    public readonly originalEvent: TOriginalEvent
  ) {
    super('durationchange');
  }
}

const StyledIconButton = styled(IconButton)`
  color: var(--secondary-color);
  padding: 6px;

  display: flex;
  align-items: center;
  justify-content: center;

  &&:not(:disabled):hover {
    background-color: var(--secondary-color-muted) !important;
    opacity: 1 !important;
    box-shadow: none !important;
    border-radius: 50%;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export type DurationSelectorUnitFieldChangeEvent<
  TOriginalEvent =
    | React.MouseEvent<HTMLButtonElement, MouseEvent>
    | React.FocusEvent<HTMLInputElement, Element>
> = DurationChangeEvent<TOriginalEvent>;

export type DurationSelectorUnitFieldProps = {
  value: string;
  unit: DurationUnitType;
  label?: string;
  hold?: boolean;
  maxValue?: number;
  minValue?: number;
  disabled?: boolean;
  onChange?: (evt: DurationSelectorUnitFieldChangeEvent, value: number) => void;
};

/**
 * Single unit time representation with increment/decrement buttons
 */
export const DurationSelectorUnitField: React.FC<DurationSelectorUnitFieldProps> = ({
  unit,
  label = unit,
  value,
  onChange,
  hold = true,
  maxValue = Infinity,
  minValue = -Infinity,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState<string>(value.toString());
  const lastTickRef = useRef<number>(0);
  const animDelayRef = useRef<number>(0);
  const rafRef = useRef<any | null>(null);
  const valueRef = useRef(value);

  const parseValue = useCallback(
    (value: string | number, truncate = true) => {
      let parsedValue = typeof value !== 'number' ? parseInt(value) : value;

      parsedValue = isNaN(parsedValue) ? 0 : parsedValue;

      if (truncate) {
        parsedValue = Math.max(0, Math.min(parsedValue, maxValue), minValue);
      }
      return parsedValue;
    },
    [maxValue, minValue]
  );

  const handleInputChange = useCallback<PickProp<DurationSelectorCustomInputProps, 'onChange'>>(
    (evt) => {
      const parsedValue = parseValue((evt.target as HTMLInputElement).value, false);
      setInputValue(`${parsedValue ?? 0}`);
    },
    [parseValue]
  );

  const handleInputBlur = useCallback<PickProp<DurationSelectorCustomInputProps, 'onBlur'>>(
    (evt) => {
      const parsedValue = parseValue(inputValue);
      setInputValue(`${parsedValue ?? 0}`);
      onChange?.(
        new DurationChangeEvent(parsedValue, unit, label, evt),
        isNaN(parsedValue) ? 0 : parsedValue
      );
    },
    [inputValue, label, onChange, parseValue, unit]
  );

  const clearTimers = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
  }, []);

  const fireChange = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>, inc: number) => {
      const newValue = parseValue(parseValue(valueRef.current) + inc);
      const changeEvent = new DurationChangeEvent(newValue, unit, label, evt);
      setInputValue(`${newValue ?? 0}`);
      onChange?.(changeEvent, newValue);
    },
    [label, onChange, parseValue, unit]
  );

  const applyOffsetChange = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>, inc: number) => {
      fireChange(evt, inc);

      if (hold) {
        clearTimers();
        animDelayRef.current = 500;

        const loop = function () {
          const curTime = Date.now();
          if (curTime - lastTickRef.current > animDelayRef.current) {
            fireChange(evt, inc);
            lastTickRef.current = curTime;
            animDelayRef.current = Math.max(animDelayRef.current * 0.5, 50);
          }
          rafRef.current = requestAnimationFrame(loop);
        };

        loop();
      }
    },
    [clearTimers, fireChange, hold]
  );

  const handleIncrement = useCallback<PickProp<IconButtonProps, 'onMouseDown'>>(
    (evt) => {
      applyOffsetChange(evt, +1);
    },
    [applyOffsetChange]
  );

  const handleDecrement = useCallback<PickProp<IconButtonProps, 'onMouseDown'>>(
    (evt) => {
      applyOffsetChange(evt, -1);
    },
    [applyOffsetChange]
  );

  // save the value in a ref so we can use it in the effect below
  valueRef.current = value;

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return (
    <FlexBox alignItems="self-start" style={{ color: 'var(--primary-text)' }}>
      <Box>
        <StyledIconButton
          disabled={disabled || parseValue(value) >= maxValue}
          onMouseDown={handleIncrement}
          onMouseLeave={clearTimers}
          onMouseUp={clearTimers}
        >
          <IconChevronUp />
        </StyledIconButton>
      </Box>
      <FlexBox alignItems="center" direction="row" gap={1}>
        <DurationSelectorCustomInput
          disabled={disabled}
          tabIndex={0}
          value={inputValue}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
        />
        <Typography>{label}</Typography>
      </FlexBox>
      <Box>
        <StyledIconButton
          disabled={disabled || parseValue(value) <= minValue}
          onMouseDown={handleDecrement}
          onMouseLeave={clearTimers}
          onMouseUp={clearTimers}
        >
          <IconChevronDown />
        </StyledIconButton>
      </Box>
    </FlexBox>
  );
};
